"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPermutations = exports.enforceCors = exports.getAllowedOriginsFromEnv = exports.isTimeoutError = exports.isAbsoluteUrl = void 0;
exports.resolveUrl = resolveUrl;
const is_server_1 = __importDefault(require("./is-server"));
/**
 * note: encodeURIComponent is available via browser (window) or natively in node.js
 * if you use another js engine for server-side rendering you may not have native encodeURIComponent
 * and would then need to install a package for that functionality
 * @param {ParsedUrlQueryInput} params query string parameters
 * @returns {string} query string
 */
function getQueryString(params) {
    return Object.keys(params)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`)
        .join('&');
}
/**
 * Resolves a base URL that may contain query string parameters and an additional set of query
 * string parameters into a unified string representation.
 * @param {string} urlBase the base URL that may contain query string parameters
 * @param {ParsedUrlQueryInput} params query string parameters
 * @returns a URL string
 * @throws {RangeError} if the provided url is an empty string
 */
function resolveUrl(urlBase, params = {}) {
    if (!urlBase) {
        throw new RangeError('url must be a non-empty string');
    }
    // This is a better way to work with URLs since it handles different user input
    // edge cases. This works in Node and all browser except IE11.
    // https://developer.mozilla.org/en-US/docs/Web/API/URL
    // TODO: Verify our browser support requirements.
    if ((0, is_server_1.default)()) {
        const url = new URL(urlBase);
        for (const key in params) {
            if ({}.hasOwnProperty.call(params, key)) {
                url.searchParams.append(key, String(params[key]));
            }
        }
        const result = url.toString();
        return result;
    }
    const qs = getQueryString(params);
    const result = urlBase.indexOf('?') !== -1 ? `${urlBase}&${qs}` : `${urlBase}?${qs}`;
    return result;
}
const isAbsoluteUrl = (url) => {
    if (!url) {
        return false;
    }
    if (typeof url !== 'string') {
        throw new TypeError('Expected a string');
    }
    return /^[a-z][a-z0-9+.-]*:/.test(url);
};
exports.isAbsoluteUrl = isAbsoluteUrl;
/**
 * Indicates whether the error is a timeout error
 * @param {unknown} error error
 * @returns {boolean} is timeout error
 */
const isTimeoutError = (error) => {
    var _a;
    return (error.code === '408' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 408 ||
        error.name === 'AbortError');
};
exports.isTimeoutError = isTimeoutError;
/**
 * Converts a string value in a regex pattern allowing wildcard matching
 * @param {string} pattern input with wildcards i.e. site.*.com
 * @returns {string} modified string that can be used as regexp input
 */
const convertToWildcardRegex = (pattern) => {
    return ('^' +
        pattern
            .replace(/\//g, '\\/')
            .replace(/\./g, '\\.')
            .replace(/\*/g, '.*') +
        '$');
};
/**
 * Gets allowed origins from JSS_ALLOWED_ORIGINS env variable
 * @returns {string[]} list of allowed origins from JSS_ALLOWED_ORIGINS env variable
 */
const getAllowedOriginsFromEnv = () => process.env.JSS_ALLOWED_ORIGINS
    ? process.env.JSS_ALLOWED_ORIGINS.replace(' ', '').split(',')
    : [];
exports.getAllowedOriginsFromEnv = getAllowedOriginsFromEnv;
/**
 * Tests origin from incoming request against allowed origins list that can be
 * set in JSS's JSS_ALLOWED_ORIGINS env variable, passed via allowedOrigins param and/or
 * be already set in Access-Control-Allow-Origin by other logic.
 * Applies Access-Control-Allow-Origin and Access-Control-Allow-Methods on match
 * Also applies Access-Control-Allow-Headers for preflight requests
 * @param {IncomingMessage} req incoming request
 * @param {OutgoingMessage} res response to set CORS headers for
 * @param {string[]} [allowedOrigins] additional list of origins to test against
 * @returns true if incoming origin matches the allowed lists, false when it does not
 */
const enforceCors = (req, res, allowedOrigins) => {
    // origin in not present for non-CORS requests (e.g. server-side) - so we skip the checks
    if (!req.headers.origin) {
        return true;
    }
    // 3 sources of allowed origins are considered:
    // the env value
    const defaultAllowedOrigins = (0, exports.getAllowedOriginsFromEnv)();
    // the allowedOrigins prop
    allowedOrigins = defaultAllowedOrigins.concat(allowedOrigins || []);
    // and the existing CORS header, if present (i.e. set by nextjs config)
    const presetCors = res.getHeader('Access-Control-Allow-Origin');
    if (presetCors) {
        allowedOrigins.push(presetCors);
    }
    const origin = req.headers.origin;
    if (origin &&
        allowedOrigins.some((allowedOrigin) => origin === allowedOrigin || new RegExp(convertToWildcardRegex(allowedOrigin)).test(origin))) {
        res.setHeader('Access-Control-Allow-Origin', origin);
        res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS, DELETE, PUT, PATCH');
        // set the allowed headers for preflight requests
        if (req.method === 'OPTIONS') {
            res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Authorization');
        }
        return true;
    }
    return false;
};
exports.enforceCors = enforceCors;
/**
 * Generates all possible permutations of an array of key-value pairs.
 * This is used to create every possible combination of URL query parameters.
 * @param {Array<[string, string]>} array - The array of key-value pairs to permute.
 * @returns {Array<Array<[string, string]>>} - A 2D array where each inner array is a unique permutation of the input.
 */
const getPermutations = (array) => {
    if (array.length <= 1)
        return [array];
    return array.flatMap((current, i) => {
        const remaining = array.filter((_, idx) => idx !== i);
        return (0, exports.getPermutations)(remaining).map((permutation) => [current, ...permutation]);
    });
};
exports.getPermutations = getPermutations;
